import type { TrackingEvent } from '@marketplace-web/observability/event-tracker-data'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { ContentSource } from 'constants/tracking/content-sources'
import { Screen } from 'constants/tracking/screens'

type UserViewItemGalleryEventArgs = {
  imagePosition: number
  imageCount: number
  itemId: number
  isLoop: boolean
  contentType: string
  galleryContainsAd: boolean
  imageView: 'fullscreen' | 'item_page'
}

type UserViewItemGalleryEventExtra = {
  image_position: number
  number_item_images: number
  item_id: number
  image_view: 'fullscreen' | 'item_page'
  is_loop: boolean
  content_type: string
  gallery_contains_ad: boolean
}

export const userViewItemGallery = (args: UserViewItemGalleryEventArgs): TrackingEvent => {
  const { imagePosition, imageCount, itemId, isLoop, contentType, galleryContainsAd, imageView } =
    args

  const extra: UserViewItemGalleryEventExtra = {
    // This value is hardcoded, because we have gallery only in a modal form
    // hence the `fullscreen` name. In apps, they have two modes to show gallery,
    // in overlay form or in the page itself, hence they have different values here.
    image_view: imageView,
    image_position: imagePosition,
    number_item_images: imageCount,
    item_id: itemId,
    is_loop: isLoop,
    content_type: contentType,
    gallery_contains_ad: galleryContainsAd,
  }

  return {
    event: 'user.view_item_gallery',
    extra,
  }
}

type ViewItemArgs = {
  itemId: number
  ownerId: number
  referrerPath?: string
  searchSessionId: string | null
  globalCatalogBrowseSessionId?: string | null
}

export const viewItemEvent = (args: ViewItemArgs) => {
  const { itemId, ownerId, referrerPath, searchSessionId, globalCatalogBrowseSessionId } = args

  return {
    event: 'user.view_item',
    extra: {
      item_id: itemId,
      item_owner_id: ownerId,
      path: referrerPath,
      search_session: searchSessionId,
      global_catalog_browse_session_id: globalCatalogBrowseSessionId || undefined,
    },
  }
}

type ViewOwnItemArgs = {
  itemId: number
  ownerId: number
  referrerPath?: string
  searchSessionId: string | null
}

export const viewOwnItemEvent = (args: ViewOwnItemArgs) => {
  const { itemId, ownerId, referrerPath, searchSessionId } = args

  return {
    event: 'user.view_current_user_item',
    extra: {
      item_id: itemId,
      item_owner_id: ownerId,
      path: referrerPath,
      search_session: searchSessionId,
    },
  }
}

type ItemAttributesEventArgs = {
  itemId: string
  attributeCode: string
  attributeValueId?: string
  details?: string
  itemPageSessionId?: string
}

type ItemAttributesEventExtra = {
  screen: string
  item_id: string
  attribute_code: string
  attribute_value_id?: string | null
  details?: string | null
  item_page_session_id?: string | null
}

export const itemAttributesViewEvent = (args: ItemAttributesEventArgs) => {
  const { itemId, attributeCode, attributeValueId, details, itemPageSessionId } = args

  const extra: ItemAttributesEventExtra = {
    screen: Screen.Item,
    item_id: itemId,
    attribute_code: attributeCode,
  }

  if (attributeValueId) extra.attribute_value_id = attributeValueId
  if (details) extra.details = details
  if (itemPageSessionId) extra.item_page_session_id = itemPageSessionId

  return {
    event: 'item_page.item_attributes.view',
    extra,
  }
}

type ItemPageChannelViewEventArgs = {
  contentSource: ContentSource
  itemId: string
}

type ItemPageChannelViewEventExtra = {
  content_source: ContentSource
  item_id: string
}

export const itemPageChannelViewEvent = (args: ItemPageChannelViewEventArgs) => {
  const { contentSource, itemId } = args

  const extra: ItemPageChannelViewEventExtra = {
    content_source: contentSource,
    item_id: itemId,
  }

  return {
    event: 'item_page.channel.view',
    extra,
  }
}

type ItemPageChannelClickEventArgs = {
  target: ClickableElement
  contentSource: ContentSource
  itemId: string
}

type ItemPageChannelClickEventExtra = {
  target: ClickableElement
  content_source: ContentSource
  item_id: string
}

export const itemPageChannelClickEvent = (args: ItemPageChannelClickEventArgs) => {
  const { target, contentSource, itemId } = args

  const extra: ItemPageChannelClickEventExtra = {
    target,
    content_source: contentSource,
    item_id: itemId,
  }

  return {
    event: 'item_page.channel.click',
    extra,
  }
}
